import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
import useAuth from 'hooks/useAuth';
import PropTypes from 'prop-types';
import React from 'react';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import routes from 'routes';

export default function HeaderLinks() {
  const { colorMode, toggleColorMode } = useColorMode();
  const { user, logout } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const navbarIcon = useColorModeValue('secondaryGray.700', 'white');
  const menuBg = useColorModeValue(
    'rgba(255,255,255,0.94)',
    'rgba(17, 23, 39, 0.92)',
  );
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const subText = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
  const shadow = useColorModeValue(
    '0 18px 44px rgba(31, 52, 112, 0.12)',
    '0 22px 54px rgba(2, 8, 23, 0.28)',
  );
  const dialogBg = useColorModeValue(
    'rgba(255,255,255,0.98)',
    'rgba(17, 23, 39, 0.98)',
  );
  const dialogBorder = useColorModeValue(
    'rgba(162,176,210,0.18)',
    'rgba(255,255,255,0.08)',
  );

  const handleThemeToggle = React.useCallback(
    (event) => {
      const root = document.documentElement;

      if (!document.startViewTransition) {
        toggleColorMode();
        return;
      }

      if (event) {
        root.style.setProperty('--x', `${event.clientX}px`);
        root.style.setProperty('--y', `${event.clientY}px`);
      } else {
        root.style.setProperty('--x', `${window.innerWidth / 2}px`);
        root.style.setProperty('--y', `${window.innerHeight / 2}px`);
      }

      document.startViewTransition(() => {
        toggleColorMode();
      });
    },
    [toggleColorMode],
  );

  const handleLogoutConfirm = React.useCallback(() => {
    onClose();
    logout();
  }, [logout, onClose]);

  return (
    <>
      <Flex
        w="auto"
        alignItems="center"
        flexDirection="row"
        bg={menuBg}
        p={{ base: '6px', md: '8px' }}
        gap={{ base: '2px', md: '0px' }}
        borderRadius="999px"
        boxShadow={shadow}
        border="1px solid"
        borderColor={useColorModeValue(
          'rgba(162,176,210,0.18)',
          'rgba(255,255,255,0.08)',
        )}
      >
        {/* <SearchBar display={{ base: 'none', md: 'block' }} me='8px' borderRadius='999px' /> */}
        <SidebarResponsive routes={routes} />

        {/* <Button
          display={{ base: 'none', md: 'inline-flex' }}
          variant='no-hover'
          bg='transparent'
          minW='unset'
          h='40px'
          px='8px'>
          <Icon as={MdNotificationsNone} color={navbarIcon} boxSize='20px' />
        </Button>

        <Button
          display={{ base: 'none', md: 'inline-flex' }}
          variant='no-hover'
          bg='transparent'
          minW='unset'
          h='40px'
          px='8px'>
          <Icon as={MdInfoOutline} color={navbarIcon} boxSize='20px' />
        </Button> */}

        <Button
          variant="no-hover"
          bg="transparent"
          minW="unset"
          h={{ base: '38px', md: '40px' }}
          px={{ base: '6px', md: '8px' }}
          onClick={handleThemeToggle}
        >
          <Icon
            color={navbarIcon}
            as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
            boxSize="19px"
          />
        </Button>

        <Menu>
          <MenuButton p="0">
            <Avatar
              _hover={{ cursor: 'pointer' }}
              color="white"
              name={user?.name || 'Admin User'}
              bg="brand.700"
              size="sm"
              w={{ base: '44px', md: '48px' }}
              h={{ base: '44px', md: '48px' }}
            />
          </MenuButton>
          <MenuList
            boxShadow={shadow}
            p="10px"
            mt="10px"
            borderRadius="22px"
            bg={menuBg}
            border="1px solid"
            borderColor={useColorModeValue(
              'rgba(162,176,210,0.18)',
              'rgba(255,255,255,0.08)',
            )}
          >
            <Flex direction="column" px="10px" py="8px">
              <Text fontWeight="800" color={textColor}>
                {user?.name || 'Super Admin'}
              </Text>
              <Text fontSize="sm" color={subText}>
                {user?.email || 'admin'}
              </Text>
            </Flex>
            <MenuItem borderRadius="16px" onClick={onOpen}>
              Sign out
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay
          bg="rgba(15, 23, 42, 0.48)"
          backdropFilter="blur(6px)"
        >
          <AlertDialogContent
            bg={dialogBg}
            borderRadius="28px"
            border="1px solid"
            borderColor={dialogBorder}
            boxShadow={shadow}
            mx="20px"
          >
            <AlertDialogHeader fontSize="xl" fontWeight="800">
              Confirm logout
            </AlertDialogHeader>

            <AlertDialogBody color={subText}>
              You’re about to sign out of the admin panel. Any unsaved changes
              on the current screen may be lost.
            </AlertDialogBody>

            <AlertDialogFooter gap="12px">
              <Button ref={cancelRef} onClick={onClose} variant="subtle">
                Cancel
              </Button>
              <Button variant="brand" onClick={handleLogoutConfirm}>
                Yes, sign out
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

HeaderLinks.propTypes = {
  secondary: PropTypes.bool,
};
